import React, { useEffect, useMemo, useState } from "react";
import { FaSync, FaFilePdf, FaSearch, FaEye } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface StudentActivity {
  studentId: number;
  studentName: string;
  homeworkSubmitted: number;
  homeworkTotal: number;
  quizSubmitted: number;
  quizTotal: number;
  writingSubmitted: number;
  writingTotal: number;
  classTestSubmitted: number;
  classTestTotal: number;
  attendancePresent: number;
  attendanceTotal: number;
  audioCount: number;
  overallPercent: number;
}

interface BatchReport {
  batchId: number;
  batchName: string;
  mentorName: string;
  startingDate: string | null;
  totalStudents: number;
  avgHomeworkPercent: number;
  avgQuizPercent: number;
  avgWritingPercent: number;
  avgClassTestPercent: number;
  avgAttendancePercent: number;
  totalAudioCount: number;
  active: boolean;
  students: StudentActivity[];
}

const pill = (submitted: number, total: number) => {
  if (total === 0) return { text: `${submitted}`, color: "bg-gray-100 text-gray-500" };
  const pct = submitted / total;
  if (pct >= 0.8) return { text: `${submitted}/${total}`, color: "bg-green-100 text-green-700" };
  if (pct >= 0.4) return { text: `${submitted}/${total}`, color: "bg-yellow-100 text-yellow-700" };
  return { text: `${submitted}/${total}`, color: "bg-red-100 text-red-700" };
};

const barColor = (pct: number) => {
  if (pct >= 80) return "bg-green-500";
  if (pct >= 40) return "bg-yellow-400";
  return "bg-red-400";
};

const overallColor = (pct: number) => {
  if (pct >= 80) return "text-green-600";
  if (pct >= 40) return "text-yellow-600";
  return "text-red-500";
};

const pctColor = (v: number): [number, number, number] =>
  v >= 80 ? [21, 128, 61] : v >= 40 ? [161, 98, 7] : [185, 28, 28];

const renderBatchPage = (doc: jsPDF, batch: BatchReport, today: string) => {
  const M = 14;
  const W = 210;

  // ── HEADER (blue bg) ─────────────────────────────────────
  doc.setFillColor(0, 159, 227);
  doc.rect(0, 0, W, 30, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  doc.text("Student Activity Report", M, 13);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(210, 240, 255);
  doc.text(`Batch: ${batch.batchName}  ·  All activity tracked`, M, 23);

  // brand top-right (on blue bg — white/light colors)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const gW = doc.getTextWidth("Guideline");
  const tW = doc.getTextWidth("2");
  const brandX = W - M - gW - tW - doc.getTextWidth("Germany");
  doc.setTextColor(255, 255, 255);  doc.text("Guideline", brandX, 11);
  doc.setTextColor(255, 130, 130);  doc.text("2", brandX + gW, 11);
  doc.setTextColor(255, 220, 100);  doc.text("Germany", brandX + gW + tW, 11);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(200, 235, 255);
  doc.text(`Generated: ${today}`, W - M, 21, { align: "right" });

  // ── META BAR (light blue bg) ──────────────────────────────
  doc.setFillColor(240, 249, 255);
  doc.rect(0, 30, W, 14, "F");
  doc.setDrawColor(186, 230, 253);
  doc.setLineWidth(0.3);
  doc.line(0, 44, W, 44);

  let x = M;
  const metaItem = (label: string, val: string, last = false) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(107, 114, 128);
    doc.text(`${label}: `, x, 40);
    x += doc.getTextWidth(`${label}: `);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(17, 24, 39);
    doc.text(val, x, 40);
    x += doc.getTextWidth(val);
    if (!last) {
      doc.setFont("helvetica", "normal");
      doc.setTextColor(209, 213, 219);
      doc.text("  |  ", x, 40);
      x += doc.getTextWidth("  |  ");
    }
  };
  metaItem("Mentor",  batch.mentorName);
  metaItem("Started", batch.startingDate || "—");
  metaItem("Audio",   String(batch.totalAudioCount), true);

  // ── STATS ROW ────────────────────────────────────────────
  x = M;
  const statItem = (label: string, val: string, color: [number,number,number], last = false) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(107, 114, 128);
    doc.text(`${label}  `, x, 53);
    x += doc.getTextWidth(`${label}  `);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text(val, x, 53);
    x += doc.getTextWidth(val);
    if (!last) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(229, 231, 235);
      doc.text("   ·   ", x, 53);
      x += doc.getTextWidth("   ·   ");
    }
  };
  statItem("Avg Attendance",  `${batch.avgAttendancePercent}%`,  pctColor(batch.avgAttendancePercent));
  statItem("Avg Homework",    `${batch.avgHomeworkPercent}%`,   pctColor(batch.avgHomeworkPercent));
  statItem("Avg Quiz",        `${batch.avgQuizPercent}%`,       pctColor(batch.avgQuizPercent));
  statItem("Avg Writing Task",`${batch.avgWritingPercent}%`,    pctColor(batch.avgWritingPercent));
  statItem("Avg Class Test",  `${batch.avgClassTestPercent}%`,  pctColor(batch.avgClassTestPercent), true);

  // ── FOCUS ALERT ─────────────────────────────────────────
  let divY = 59;
  const struggling = batch.students.filter(s => s.overallPercent < 50);
  if (struggling.length > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const dotW = doc.getTextWidth("  ·  ");
    const lineH = 5.5;
    const labelPrefix = "! Needs more focus:";
    doc.setFont("helvetica", "bold");
    const prefixW = doc.getTextWidth(labelPrefix) + 3;

    // Build wrapped lines
    doc.setFont("helvetica", "normal");
    const lines: string[][] = [[]];
    let lx = M + prefixW;
    for (const s of struggling) {
      const label = `${s.studentName} (${s.overallPercent}%)`;
      const lw = doc.getTextWidth(label);
      if (lx + lw > W - M && lines[lines.length - 1].length > 0) {
        lines.push([]);
        lx = M + 4;
      }
      lines[lines.length - 1].push(label);
      lx += lw + dotW;
    }

    const boxH = 3 + lines.length * lineH + 2;
    doc.setFillColor(255, 251, 235);
    doc.rect(0, divY, W, boxH, "F");
    doc.setDrawColor(253, 230, 138);
    doc.setLineWidth(0.2);
    doc.line(0, divY, W, divY);
    doc.line(0, divY + boxH, W, divY + boxH);

    let cy = divY + lineH;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(180, 83, 9);
    doc.text(labelPrefix, M, cy);
    let fx = M + prefixW;

    for (let li = 0; li < lines.length; li++) {
      if (li > 0) { cy += lineH; fx = M + 4; }
      for (let ni = 0; ni < lines[li].length; ni++) {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(180, 83, 9);
        doc.text(lines[li][ni], fx, cy);
        fx += doc.getTextWidth(lines[li][ni]);
        if (ni < lines[li].length - 1 || li < lines.length - 1) {
          doc.setTextColor(209, 213, 219);
          doc.text("  ·  ", fx, cy);
          fx += dotW;
        }
      }
    }
    divY += boxH;
  }

  // ── DIVIDER ──────────────────────────────────────────────
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.3);
  doc.line(0, divY, W, divY);

  // ── TABLE ─────────────────────────────────────────────────
  const rows = batch.students.map((s, i) => [
    i + 1,
    s.studentName,
    s.attendanceTotal > 0 ? `${s.attendancePresent}/${s.attendanceTotal}` : `${s.attendancePresent}`,
    s.homeworkTotal > 0 ? `${s.homeworkSubmitted}/${s.homeworkTotal}` : `${s.homeworkSubmitted}`,
    s.quizTotal > 0 ? `${s.quizSubmitted}/${s.quizTotal}` : `${s.quizSubmitted}`,
    s.writingTotal > 0 ? `${s.writingSubmitted}/${s.writingTotal}` : `${s.writingSubmitted}`,
    s.classTestTotal > 0 ? `${s.classTestSubmitted}/${s.classTestTotal}` : `${s.classTestSubmitted}`,
    s.audioCount,
    `${s.overallPercent}%`,
  ]);

  autoTable(doc, {
    startY: divY + 4,
    margin: { left: M, right: M },
    head: [["#", "Student", "Attendance", "Homework", "Quiz", "Writing Task", "Class Test", "Audio", "Overall"]],
    body: rows,
    styles: {
      fontSize: 9,
      cellPadding: { top: 4.5, bottom: 4.5, left: 4, right: 4 },
      lineWidth: 0.25,
      lineColor: [0, 159, 227],
      textColor: [25, 35, 50],
    },
    headStyles: {
      fillColor: [0, 159, 227],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8.5,
      lineWidth: 0.25,
      lineColor: [0, 130, 190],
      cellPadding: { top: 5, bottom: 5, left: 4, right: 4 },
    },
    alternateRowStyles: { fillColor: [235, 248, 255] },
    columnStyles: {
      0: { cellWidth: 10, halign: "center", textColor: [160, 170, 185] },
      1: { fontStyle: "bold" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "center" },
      5: { halign: "center" },
      6: { halign: "center" },
      7: { halign: "center" },
      8: { halign: "center", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      if (data.section !== "body") return;
      const s = batch.students[data.row.index];
      if (!s) return;
      const colorCell = (sub: number, tot: number) => {
        if (tot === 0) return;
        const p = sub / tot;
        if (p >= 0.8) { data.cell.styles.fillColor = [220, 252, 231]; data.cell.styles.textColor = [21, 128, 61]; }
        else if (p >= 0.4) { data.cell.styles.fillColor = [254, 249, 195]; data.cell.styles.textColor = [161, 98, 7]; }
        else { data.cell.styles.fillColor = [254, 226, 226]; data.cell.styles.textColor = [185, 28, 28]; }
        data.cell.styles.fontStyle = "bold";
      };
      if (data.column.index === 2) colorCell(s.attendancePresent, s.attendanceTotal);
      else if (data.column.index === 3) colorCell(s.homeworkSubmitted, s.homeworkTotal);
      else if (data.column.index === 4) colorCell(s.quizSubmitted, s.quizTotal);
      else if (data.column.index === 5) colorCell(s.writingSubmitted, s.writingTotal);
      else if (data.column.index === 6) colorCell(s.classTestSubmitted, s.classTestTotal);
      else if (data.column.index === 8) {
        data.cell.styles.textColor = pctColor(s.overallPercent);
      }
    },
  });
};

const addFooters = (doc: jsPDF) => {
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(210, 220, 232);
    doc.setLineWidth(0.3);
    doc.line(14, 285, 196, 285);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const fgW = doc.getTextWidth("Guideline");
    const ftW = doc.getTextWidth("2");
    doc.setTextColor(15, 25, 45);
    doc.text("Guideline", 14, 291);
    doc.setTextColor(220, 38, 38);
    doc.text("2", 14 + fgW, 291);
    doc.setTextColor(180, 130, 10);
    doc.text("Germany", 14 + fgW + ftW, 291);
    doc.setTextColor(160, 172, 188);
    doc.text("  —  Confidential", 14 + fgW + ftW + doc.getTextWidth("Germany"), 291);
    doc.text(`Page ${i} of ${pageCount}`, 196, 291, { align: "right" });
  }
};

const buildBatchDoc = (batch: BatchReport): jsPDF => {
  const doc = new jsPDF();
  renderBatchPage(doc, batch, new Date().toLocaleDateString("en-GB"));
  addFooters(doc);
  return doc;
};

const buildAllDoc = (batches: BatchReport[]): jsPDF => {
  const doc = new jsPDF();
  const today = new Date().toLocaleDateString("en-GB");
  batches.forEach((batch, i) => {
    if (i > 0) doc.addPage();
    renderBatchPage(doc, batch, today);
  });
  addFooters(doc);
  return doc;
};

const generateBatchPDF = (batch: BatchReport) =>
  buildBatchDoc(batch).save(`Activity_Report_${batch.batchName.replace(/\s+/g, "_")}.pdf`);

const previewBatchPDF = (batch: BatchReport) =>
  window.open(buildBatchDoc(batch).output("bloburl"), "_blank");

const generateAllPDF = (batches: BatchReport[]) => {
  if (batches.length === 0) return;
  buildAllDoc(batches).save("Activity_Report_All_Batches.pdf");
};

const previewAllPDF = (batches: BatchReport[]) => {
  if (batches.length === 0) return;
  window.open(buildAllDoc(batches).output("bloburl"), "_blank");
};

const AdminActivityReport: React.FC = () => {
  const [batches, setBatches] = useState<BatchReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterBatch, setFilterBatch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/reports/activity`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setBatches(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredBatches = useMemo(() => {
    return batches
      .filter(b => !filterBatch || b.batchId === Number(filterBatch))
      .map(b => ({
        ...b,
        students: b.students.filter(s =>
          !search || s.studentName.toLowerCase().includes(search.toLowerCase())
        ),
      }))
      .filter(b => b.students.length > 0);
  }, [batches, filterBatch, search]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaFilePdf />}
        title="Student Activity Report"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Activity Report" },
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">All batches · Homework · Quiz · Writing · Audio</p>
        <div className="flex gap-2">
          <button onClick={fetchData} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition-colors disabled:opacity-50">
            <FaSync className={loading ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button onClick={() => previewAllPDF(filteredBatches)} disabled={loading || filteredBatches.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
            <FaEye /> <span className="hidden sm:inline">Preview All</span>
          </button>
          <button onClick={() => generateAllPDF(filteredBatches)} disabled={loading || filteredBatches.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-[#009fe3] text-white rounded-lg text-sm font-semibold hover:bg-[#0077b6] transition-colors disabled:opacity-50">
            <FaFilePdf /> <span className="hidden sm:inline">Download All</span>
          </button>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Summary cards */}
        {!loading && batches.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total Batches", value: batches.length, icon: "📦" },
              { label: "Total Students", value: batches.reduce((s, b) => s + b.totalStudents, 0), icon: "👥" },
              { label: "Avg Homework", value: `${Math.round(batches.reduce((s, b) => s + b.avgHomeworkPercent, 0) / batches.length)}%`, icon: "📝" },
              { label: "Avg Quiz", value: `${Math.round(batches.reduce((s, b) => s + b.avgQuizPercent, 0) / batches.length)}%`, icon: "🧠" },
            ].map(c => (
              <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                <span className="text-2xl">{c.icon}</span>
                <div>
                  <p className="text-xs text-gray-500 font-medium">{c.label}</p>
                  <p className="text-xl font-bold text-gray-900">{c.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="flex items-center gap-2 flex-1 border border-gray-200 rounded-lg px-3 py-2">
            <FaSearch className="text-gray-400 shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search student name…"
              className="w-full text-sm border-0 outline-none" />
          </div>
          <select value={filterBatch} onChange={e => setFilterBatch(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#009fe3]">
            <option value="">All Batches</option>
            {batches.filter(b => b.active).map(b => <option key={b.batchId} value={b.batchId}>{b.batchName}</option>)}
          </select>
          {(search || filterBatch) && (
            <button onClick={() => { setSearch(""); setFilterBatch(""); }}
              className="text-xs text-gray-500 hover:text-gray-800 underline">Clear</button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <FaSync className="animate-spin text-4xl text-[#009fe3]" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">{error}</div>
        ) : filteredBatches.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">No data found.</div>
        ) : (
          filteredBatches.map(batch => (
            <div key={batch.batchId} className="mb-8">
              {/* Batch header */}
              <div className="bg-white border border-gray-200 border-b-0 rounded-t-xl px-4 sm:px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-base sm:text-lg font-bold text-gray-900">{batch.batchName}</span>
                      <span className="bg-[#009fe3] text-white text-xs font-bold px-2.5 py-0.5 rounded-full shrink-0">
                        {batch.totalStudents} students
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                      <span>Mentor: <b className="text-gray-700">{batch.mentorName}</b></span>
                      {batch.startingDate && <span>Started: <b className="text-gray-700">{batch.startingDate}</b></span>}
                      <span>Attendance: <b className="text-gray-700">{batch.avgAttendancePercent}%</b></span>
                      <span>Homework: <b className="text-gray-700">{batch.avgHomeworkPercent}%</b></span>
                      <span>Quiz: <b className="text-gray-700">{batch.avgQuizPercent}%</b></span>
                      <span>Writing: <b className="text-gray-700">{batch.avgWritingPercent}%</b></span>
                      <span>Class Test: <b className="text-gray-700">{batch.avgClassTestPercent}%</b></span>
                      <span>Audio: <b className="text-gray-700">{batch.totalAudioCount}</b></span>
                    </div>
                    {batch.students.length > 0 && (
                      <div className="text-xs mt-1">
                        <span className="text-green-600 font-semibold">★ Top: {batch.students[0].studentName} ({batch.students[0].overallPercent}%)</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                    <button onClick={() => previewBatchPDF(batch)}
                      className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs sm:text-sm font-semibold rounded-lg transition-colors">
                      <FaEye /> <span>Preview</span>
                    </button>
                    <button onClick={() => generateBatchPDF(batch)}
                      className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors">
                      <FaFilePdf /> <span>PDF</span>
                    </button>
                  </div>
                </div>
              </div>


              {/* Focus Alert — always use unfiltered student list */}
              {(() => {
                const allStudents = batches.find(b => b.batchId === batch.batchId)?.students ?? batch.students;
                const struggling = allStudents.filter(s => s.overallPercent < 50);
                if (struggling.length === 0) return null;
                return (
                  <div className="border border-amber-200 border-t-0 bg-amber-50 px-4 py-3 flex flex-wrap items-start gap-2">
                    <span className="text-amber-700 font-bold text-xs shrink-0 mt-0.5">⚠ Needs more focus:</span>
                    <div className="flex flex-wrap gap-2">
                      {struggling.map(s => (
                        <span key={s.studentId} className="inline-flex items-center gap-1 bg-white border border-amber-200 text-amber-800 text-xs font-semibold px-2.5 py-1 rounded-full">
                          {s.studentName}
                          <span className="text-red-500 font-bold">{s.overallPercent}%</span>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Table */}
              <div className="bg-white border border-gray-200 rounded-b-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-8 sticky left-0 bg-gray-50 z-10">#</th>
                        <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase sticky left-8 bg-gray-50 z-10">Student</th>
                        <th className="px-2 sm:px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Attendance</th>
                        <th className="px-2 sm:px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Homework</th>
                        <th className="px-2 sm:px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Quiz</th>
                        <th className="px-2 sm:px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Writing</th>
                        <th className="px-2 sm:px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Class Test</th>
                        <th className="px-2 sm:px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Audio</th>
                        <th className="px-2 sm:px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Overall</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {batch.students.map((s, idx) => {
                        const hw  = pill(s.homeworkSubmitted, s.homeworkTotal);
                        const qz  = pill(s.quizSubmitted, s.quizTotal);
                        const wt  = pill(s.writingSubmitted, s.writingTotal);
                        const ct  = pill(s.classTestSubmitted, s.classTestTotal);
                        const att = pill(s.attendancePresent, s.attendanceTotal);
                        const hwPct  = s.homeworkTotal   > 0 ? (s.homeworkSubmitted  / s.homeworkTotal)   * 100 : 0;
                        const qzPct  = s.quizTotal       > 0 ? (s.quizSubmitted      / s.quizTotal)       * 100 : 0;
                        const wtPct  = s.writingTotal    > 0 ? (s.writingSubmitted   / s.writingTotal)    * 100 : 0;
                        const ctPct  = s.classTestTotal  > 0 ? (s.classTestSubmitted / s.classTestTotal)  * 100 : 0;
                        const attPct = s.attendanceTotal > 0 ? (s.attendancePresent  / s.attendanceTotal) * 100 : 0;
                        const rowBg  = idx % 2 === 0 ? "bg-white" : "bg-gray-50/50";
                        const pillCell = (p: ReturnType<typeof pill>, pct: number) => (
                          <td className="px-2 sm:px-4 py-2 sm:py-3">
                            <div className="flex flex-col items-center gap-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${p.color}`}>{p.text}</span>
                              <div className="w-10 sm:w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${barColor(pct)}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </td>
                        );
                        return (
                          <tr key={s.studentId} className={rowBg}>
                            <td className={`px-2 sm:px-4 py-2 sm:py-3 text-gray-500 font-medium sticky left-0 z-10 ${rowBg}`}>{idx + 1}</td>
                            <td className={`px-2 sm:px-4 py-2 sm:py-3 sticky left-8 z-10 ${rowBg}`}>
                              <p className="font-semibold text-gray-900 text-xs sm:text-sm whitespace-nowrap">{s.studentName}</p>
                            </td>
                            {pillCell(att, attPct)}
                            {pillCell(hw, hwPct)}
                            {pillCell(qz, qzPct)}
                            {pillCell(wt, wtPct)}
                            {pillCell(ct, ctPct)}
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                              <span className="font-semibold text-gray-700">{s.audioCount}</span>
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                              <span className={`font-bold text-sm sm:text-base ${overallColor(s.overallPercent)}`}>
                                {s.overallPercent}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
};

export default AdminActivityReport;
